import { Link } from "react-router-dom";
import {
  ArrowRight,
  ArrowUpRight,
  Check,
  Download,
  FileText,
  Gauge,
  Headset,
  MapPin,
  Quote,
  ShieldCheck,
  UserCog,
} from "lucide-react";
import { Icon } from "../components/DynamicIcon";
import { Button } from "../components/Button";
import { Reveal, Overline } from "../components/Reveal";
import { CTASection } from "../components/CTASection";
import { PlanCards } from "../components/itsupport/PlanCards";
import { sampleReportUrl } from "../lib/api";
import { trackEvent } from "../lib/analytics";
import {
  COMPANY,
  IMAGES,
  PROBLEMS,
  METHODOLOGY,
  INDUSTRIES,
  DIFFERENTIATORS,
  LOCATIONS,
  FOUNDER,
} from "../data/site";

const BUYING_PATHS = [
  {
    icon: Headset,
    name: "Business IT Support",
    intent: "\u201cWe need dependable, day-to-day IT help.\u201d",
    body: "Responsive help desk, device management, Microsoft 365, and security from one accountable Oklahoma team — plans from $495/month including up to 5 users.",
    cta: "Explore IT Support Plans",
    to: "/business-it-support",
    featured: true,
    event: "it_support_cta_click",
  },
  {
    icon: Gauge,
    name: "Technology Health Assessment",
    intent: "\u201cI know something needs attention, but I'm not sure what.\u201d",
    body: "A fixed-price review of the technology your business relies on — health score, top risks and opportunities, and a prioritized plan. Starting at $495.",
    cta: "Start An Assessment",
    to: "/technology-health-snapshot",
    event: "snapshot_cta_click",
  },
  {
    icon: ShieldCheck,
    name: "Cybersecurity Assessment",
    intent: "\u201cI'm concerned about risk, controls, access, or insurance.\u201d",
    body: "A focused review of security fundamentals — access, backups, Microsoft 365, and incident readiness — translated into business priorities.",
    cta: "Assess My Security",
    to: "/assessments/cybersecurity",
  },
  {
    icon: UserCog,
    name: "Technology Strategy / Fractional CTO",
    intent: "\u201cWe need senior guidance and a roadmap.\u201d",
    body: "Ongoing executive technology leadership — roadmaps, vendor selection, modernization, and budget-aligned decisions without a full-time hire.",
    cta: "Discuss Technology Leadership",
    to: "/services/fractional-cto",
  },
];

const OFFER_DELIVERABLES = [
  "Overall Technology Health Score",
  "Top 5 risks and top 5 opportunities",
  "Immediate, 30-day, 90-day, and 12-month plan",
  "Estimated business impact in plain language",
];

const trackSampleReport = (location) => {
  trackEvent("sample_report_view", { page_path: "/", cta_location: location });
};

export default function Home() {
  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative overflow-hidden bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center py-16 lg:py-24">
            <div className="max-w-xl">
              <Reveal>
                <Overline>Oklahoma Business IT Support & Technology Advisory</Overline>
              </Reveal>
              <Reveal delay={0.08}>
                <h1 className="mt-5 text-4xl sm:text-5xl lg:text-6xl font-bold text-charcoal tracking-tight leading-[1.05]">
                  Your IT department—<span className="text-forest">without hiring</span> an IT department
                </h1>
              </Reveal>
              <Reveal delay={0.16}>
                <p className="mt-6 text-lg text-charcoal/75 leading-relaxed">
                  Silvertree gives Oklahoma small businesses responsive, plain-English IT support —
                  help desk, devices, Microsoft 365, and security — plus the strategy and assessments
                  to make confident technology decisions.
                </p>
              </Reveal>
              <Reveal delay={0.2}>
                <p
                  className="mt-6 inline-flex items-center gap-2 rounded-full bg-forest/8 border border-forest/20 px-4 py-2 text-sm font-semibold text-forest"
                  data-testid="hero-offer-line"
                >
                  <Headset className="h-4 w-4" /> Business IT support — from $495/month, includes up to 5 users
                </p>
              </Reveal>
              <Reveal delay={0.24}>
                <div className="mt-6 flex flex-col sm:flex-row gap-4">
                  <Button
                    to="/business-it-support#it-support-form"
                    size="lg"
                    data-testid="hero-start-assessment-btn"
                    onClick={() =>
                      trackEvent("it_support_cta_click", {
                        cta_label: "Schedule a 15-Minute Technology Check",
                        cta_location: "home_hero",
                        page_path: "/",
                      })
                    }
                  >
                    Schedule a 15-Minute Technology Check <ArrowRight className="h-5 w-5" />
                  </Button>
                  <Button
                    to="/business-it-support#plans"
                    variant="secondary"
                    size="lg"
                    data-testid="hero-compare-plans-btn"
                    onClick={() =>
                      trackEvent("plan_compare_interaction", {
                        cta_label: "Compare Support Plans",
                        cta_location: "home_hero",
                        page_path: "/",
                      })
                    }
                  >
                    Compare Support Plans
                  </Button>
                </div>
              </Reveal>
              <Reveal delay={0.28}>
                <div className="mt-5 text-sm text-charcoal/70">
                  <span>Not ready yet? </span>
                  <Link
                    to="/contact?interest=IT%20Readiness%20Checklist"
                    className="font-medium text-forest hover:underline inline-flex items-center gap-1"
                    data-testid="hero-checklist-link"
                  >
                    <Download className="h-3.5 w-3.5" /> Get the free IT Readiness Checklist
                  </Link>
                </div>
              </Reveal>
              <Reveal delay={0.32}>
                <div className="mt-10 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-charcoal/70">
                  <span className="flex items-center gap-2">
                    <Check className="h-4 w-4 text-forest" /> Practical guidance
                  </span>
                  <span className="flex items-center gap-2">
                    <Check className="h-4 w-4 text-forest" /> Reduced technology risk
                  </span>
                  <span className="flex items-center gap-2">
                    <Check className="h-4 w-4 text-forest" /> Measurable outcomes
                  </span>
                </div>
              </Reveal>
            </div>

            <Reveal delay={0.2}>
              <div className="relative">
                <div className="absolute -top-6 -left-6 w-28 h-28 bg-forest/5 rounded-2xl -z-0" />
                <div className="absolute -bottom-6 -right-6 w-36 h-36 bg-gold/10 rounded-2xl -z-0" />
                <img
                  src={IMAGES.heroMeeting}
                  alt="Executives reviewing technology strategy in a modern office"
                  width="1200"
                  height="800"
                  fetchPriority="high"
                  className="relative rounded-2xl shadow-2xl object-cover w-full h-[420px] lg:h-[520px]"
                />
                <div className="absolute bottom-6 left-6 right-6 bg-white/95 backdrop-blur-sm border border-silver/30 rounded-xl p-5 shadow-lg">
                  <p className="font-editorial text-charcoal text-sm leading-relaxed">
                    "{COMPANY.promise}"
                  </p>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* TRUST / DIFFERENTIATION STRIP */}
      <section className="border-y border-silver/30 bg-[#FAFBFB]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <p className="text-center text-sm text-charcoal/70">
            <span className="text-forest font-medium">Oklahoma-based IT support and technology advisory.</span>
            {" "}Responsive help desk, managed IT, and security for growing businesses — with a 30-minute response promise for qualifying urgent requests.
          </p>
        </div>
      </section>

      {/* PROBLEM SECTION */}
      <section className="py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <Reveal>
              <img
                src={IMAGES.workshop}
                alt="A team mapping out business processes during a planning workshop"
                className="rounded-2xl shadow-xl object-cover w-full h-[400px]"
              />
            </Reveal>
            <div>
              <Reveal>
                <Overline>The Challenge</Overline>
                <h2 className="mt-4 text-3xl lg:text-4xl font-bold text-charcoal tracking-tight">
                  Technology decisions are harder than ever
                </h2>
                <p className="mt-5 text-lg text-charcoal/75 leading-relaxed">
                  Growing organizations face mounting pressure to adopt AI, strengthen
                  cybersecurity, modernize operations, and choose the right vendors — often
                  without a trusted advisor at the table.
                </p>
              </Reveal>
              <div className="mt-8 space-y-4">
                {[
                  "Reactive technology decisions made one crisis at a time",
                  "Overlapping tools, rising costs, and unclear return",
                  "Uncertainty about AI, security, and what to prioritize",
                  "No clear plan connecting technology spend to business goals",
                ].map((item, i) => (
                  <Reveal key={item} delay={i * 0.08}>
                    <div className="flex items-start gap-3">
                      <span className="mt-1 h-5 w-5 rounded-full bg-forest/10 flex items-center justify-center shrink-0">
                        <Check className="h-3 w-3 text-forest" />
                      </span>
                      <span className="text-charcoal/80">{item}</span>
                    </div>
                  </Reveal>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* IT SUPPORT PLANS */}
      <section className="py-20 lg:py-28 bg-[#FAFBFB] border-y border-silver/30" data-testid="home-it-plans">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <div className="max-w-2xl">
              <Overline>IT Support Plans</Overline>
              <h2 className="mt-4 text-3xl lg:text-4xl font-bold text-charcoal tracking-tight">
                One team. Three plans. Prices you can see.
              </h2>
              <p className="mt-5 text-lg text-charcoal/75 leading-relaxed">
                Whether you need someone to call when things break or a fully outsourced IT
                department with 24/7 security watching — there's a plan that fits.
              </p>
            </div>
          </Reveal>
          <div className="mt-12">
            <PlanCards pagePath="/" compact />
          </div>
          <Reveal>
            <div className="mt-10 text-center">
              <Button
                to="/business-it-support#plans"
                variant="secondary"
                data-testid="home-plans-compare-cta"
                onClick={() =>
                  trackEvent("plan_compare_interaction", {
                    cta_label: "Compare all plan details",
                    cta_location: "home_plans_section",
                    page_path: "/",
                  })
                }
              >
                Compare all plan details <ArrowRight className="h-4 w-4" />
              </Button>
            </div>
          </Reveal>
        </div>
      </section>

      {/* TECHNOLOGY HEALTH ASSESSMENT OFFER */}
      <section className="py-16 lg:py-20 bg-forest" data-testid="home-snapshot-promo">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-[1.4fr_1fr] gap-10 items-center">
          <Reveal>
            <p className="text-xs font-semibold uppercase tracking-wider text-gold">Start Here — Technology Health Assessment</p>
            <h2 className="mt-2 text-2xl sm:text-3xl font-bold text-white">
              Know what to fix first — starting at $495.
            </h2>
            <p className="mt-3 text-white/80 leading-relaxed max-w-2xl">
              A fixed-price review of the technology your business relies on. One clear review,
              one practical plan — usable with Silvertree, your current provider, or your own team.
            </p>
            <ul className="mt-6 grid sm:grid-cols-2 gap-x-8 gap-y-2.5">
              {OFFER_DELIVERABLES.map((d) => (
                <li key={d} className="flex items-start gap-2.5 text-sm text-white/85">
                  <Check className="h-4 w-4 text-gold shrink-0 mt-0.5" /> {d}
                </li>
              ))}
            </ul>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="flex flex-col gap-3 lg:items-end">
              <Button
                to="/technology-health-snapshot"
                variant="gold"
                size="lg"
                data-testid="home-snapshot-promo-cta"
                className="whitespace-nowrap w-full sm:w-auto"
                onClick={() =>
                  trackEvent("snapshot_cta_click", {
                    cta_label: "Start An Assessment",
                    cta_location: "home_offer_section",
                    page_path: "/",
                  })
                }
              >
                Start An Assessment <ArrowRight className="h-4 w-4" />
              </Button>
              <a
                href={sampleReportUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-sm font-medium text-white/85 hover:text-white underline underline-offset-4 decoration-white/40"
                data-testid="home-offer-sample-report-link"
                onClick={() => trackSampleReport("home_offer_section")}
              >
                <FileText className="h-4 w-4" /> View a Sample Report (PDF)
              </a>
            </div>
          </Reveal>
        </div>
      </section>

      {/* PROOF / TRUST */}
      <section className="py-20 lg:py-28 bg-[#FAFBFB] border-b border-silver/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <div className="max-w-2xl">
              <Overline>Why Clients Trust Us</Overline>
              <h2 className="mt-4 text-3xl lg:text-4xl font-bold text-charcoal tracking-tight">
                Proof, not promises
              </h2>
              <p className="mt-5 text-lg text-charcoal/75 leading-relaxed">
                We earn trust the same way we deliver work — with structure, transparency, and a
                relentless focus on measurable outcomes.
              </p>
            </div>
          </Reveal>
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: "FileText", title: "Tangible deliverables", body: "Roadmaps, assessments, and plans you can act on — see exactly what you get in our sample report." },
              { icon: "Workflow", title: "A proven methodology", body: "Every engagement follows our six-phase, business-first methodology." },
              { icon: "ShieldCheck", title: "Risk-aware approach", body: "We translate technology risk into clear business priorities and next steps." },
              { icon: "Handshake", title: "Honest engagement", body: "Clear scope and supportable claims. We won't overpromise or invent results." },
            ].map((p, i) => (
              <Reveal key={p.title} delay={i * 0.08}>
                <div className="h-full p-7 rounded-xl border border-silver/40 bg-white">
                  <div className="h-12 w-12 rounded-lg bg-forest/8 text-forest flex items-center justify-center">
                    <Icon name={p.icon} className="h-6 w-6" />
                  </div>
                  <h3 className="mt-5 text-base font-semibold text-charcoal">{p.title}</h3>
                  <p className="mt-2 text-sm text-charcoal/70 leading-relaxed">{p.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.15}>
            <div className="mt-10">
              <a
                href={sampleReportUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-sm font-semibold text-forest hover:underline"
                data-testid="home-proof-sample-report-link"
                onClick={() => trackSampleReport("home_proof_section")}
              >
                <FileText className="h-4 w-4" /> See a sample Assessment report <ArrowRight className="h-4 w-4" />
              </a>
            </div>
          </Reveal>
        </div>
      </section>

      {/* PROBLEMS WE SOLVE PREVIEW */}
      <section className="py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <div className="max-w-2xl">
              <Overline>Problems We Solve</Overline>
              <h2 className="mt-4 text-3xl lg:text-4xl font-bold text-charcoal tracking-tight">
                Not sure what to call it? Start with the problem.
              </h2>
              <p className="mt-5 text-lg text-charcoal/75 leading-relaxed">
                Many leaders search by symptom, not service. Find the challenge that sounds like
                yours — we'll point you to the right path.
              </p>
            </div>
          </Reveal>
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {PROBLEMS.slice(0, 6).map((p, i) => (
              <Reveal key={p.slug} delay={(i % 3) * 0.06}>
                <Link
                  to={`/problems/${p.slug}`}
                  data-testid={`home-problem-${p.slug}`}
                  className="group flex items-start gap-4 h-full p-6 rounded-xl border border-silver/40 hover:border-forest/40 hover:bg-forest/[0.02] transition-all"
                >
                  <span className="mt-0.5 h-9 w-9 rounded-lg bg-deepblue/8 text-deepblue flex items-center justify-center shrink-0 group-hover:bg-deepblue group-hover:text-white transition-colors">
                    <Icon name="HelpCircle" className="h-5 w-5" />
                  </span>
                  <span>
                    <span className="block font-medium text-charcoal group-hover:text-forest transition-colors">
                      "{p.title}"
                    </span>
                    <span className="mt-1.5 block text-sm text-charcoal/70 leading-relaxed">{p.angle}</span>
                  </span>
                </Link>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.15}>
            <div className="mt-10">
              <Button to="/problems" variant="secondary" data-testid="home-problems-cta">
                See all the problems we solve <ArrowRight className="h-5 w-5" />
              </Button>
            </div>
          </Reveal>
        </div>
      </section>

      {/* FOUR BUYING PATHS */}
      <section className="py-20 lg:py-28 bg-[#FAFBFB] border-y border-silver/30" data-testid="home-buying-paths">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <div className="max-w-2xl">
              <Overline>Where To Start</Overline>
              <h2 className="mt-4 text-3xl lg:text-4xl font-bold text-charcoal tracking-tight">
                Four clear ways to work with Silvertree
              </h2>
              <p className="mt-5 text-lg text-charcoal/75 leading-relaxed">
                You don't have to choose among a dozen consulting services. Start with the path
                that matches what's on your mind.
              </p>
            </div>
          </Reveal>
          <div className="mt-14 grid sm:grid-cols-2 gap-6">
            {BUYING_PATHS.map((p, i) => (
              <Reveal key={p.name} delay={(i % 2) * 0.07}>
                <div
                  data-testid={`buying-path-${i}`}
                  className={`flex flex-col h-full rounded-xl p-7 lg:p-8 border transition-all duration-300 ${
                    p.featured
                      ? "bg-forest text-white border-forest shadow-[0_16px_50px_rgb(35,79,61,0.18)]"
                      : "bg-white border-silver/40 hover:border-forest/40 hover:shadow-[0_12px_40px_rgb(0,0,0,0.06)]"
                  }`}
                >
                  <div className="flex items-start gap-4">
                    <div
                      className={`h-12 w-12 rounded-lg flex items-center justify-center shrink-0 ${
                        p.featured ? "bg-white/10 text-gold" : "bg-forest/8 text-forest"
                      }`}
                    >
                      <p.icon className="h-6 w-6" />
                    </div>
                    <div>
                      <h3 className={`text-lg font-semibold ${p.featured ? "text-white" : "text-charcoal"}`}>{p.name}</h3>
                      <p className={`mt-1 text-sm font-editorial italic ${p.featured ? "text-gold" : "text-deepblue"}`}>{p.intent}</p>
                    </div>
                  </div>
                  <p className={`mt-4 text-sm leading-relaxed flex-1 ${p.featured ? "text-white/80" : "text-charcoal/70"}`}>
                    {p.body}
                  </p>
                  <div className="mt-6">
                    <Button
                      to={p.to}
                      variant={p.featured ? "gold" : "secondary"}
                      className="w-full sm:w-auto"
                      data-testid={`buying-path-cta-${i}`}
                      onClick={
                        p.event
                          ? () =>
                              trackEvent(p.event, {
                                cta_label: p.cta,
                                cta_location: "home_buying_paths",
                                page_path: "/",
                              })
                          : undefined
                      }
                    >
                      {p.cta} <ArrowRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal>
            <div className="mt-10 text-center">
              <Link
                to="/services"
                className="inline-flex items-center gap-1.5 text-sm font-medium text-charcoal/70 hover:text-forest transition-colors"
                data-testid="home-all-services-cta"
              >
                Need something more specific? View all 12 services
                <ArrowUpRight className="h-4 w-4" />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      {/* DIFFERENTIATION */}
      <section className="py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <div className="max-w-2xl">
              <Overline>Why Silvertree</Overline>
              <h2 className="mt-4 text-3xl lg:text-4xl font-bold text-charcoal tracking-tight">
                A different kind of technology partner
              </h2>
              <p className="mt-5 text-lg text-charcoal/75 leading-relaxed">
                We're not an IT support company, a software shop, or a large consulting firm.
                We occupy the advisory space in between.
              </p>
            </div>
          </Reveal>
          <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {DIFFERENTIATORS.map((d, i) => (
              <Reveal key={d.title} delay={i * 0.08}>
                <div className="h-full p-7 rounded-xl border border-silver/40 bg-white">
                  <div className="h-11 w-11 rounded-lg bg-deepblue/8 text-deepblue flex items-center justify-center">
                    <Icon name={d.icon} className="h-5 w-5" />
                  </div>
                  <h3 className="mt-5 text-base font-semibold text-charcoal">{d.title}</h3>
                  <p className="mt-2 text-sm text-charcoal/70 leading-relaxed">{d.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* FOUNDER */}
      <section className="py-20 lg:py-28 bg-[#FAFBFB] border-y border-silver/30" data-testid="home-founder-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-5 gap-12 lg:gap-16 items-center">
            <Reveal className="lg:col-span-2">
              <div className="relative max-w-sm mx-auto lg:mx-0">
                <img
                  src={FOUNDER.headshot}
                  alt={`${FOUNDER.name}, ${FOUNDER.title} of Silvertree Solutions`}
                  width="533"
                  height="800"
                  loading="lazy"
                  className="rounded-2xl shadow-xl object-cover w-full"
                  data-testid="founder-headshot"
                />
                <div className="absolute -bottom-5 left-5 right-5 bg-white border border-silver/40 rounded-xl px-5 py-4 shadow-lg">
                  <p className="font-semibold text-charcoal">{FOUNDER.name}</p>
                  <p className="text-sm text-charcoal/70">{FOUNDER.title}</p>
                </div>
              </div>
            </Reveal>
            <div className="lg:col-span-3">
              <Reveal>
                <Overline>Founder-Led Advisory</Overline>
                <h2 className="mt-4 text-3xl lg:text-4xl font-bold text-charcoal tracking-tight">
                  Why founder-led matters
                </h2>
                <p className="mt-5 text-lg text-charcoal/75 leading-relaxed">{FOUNDER.summary}</p>
                <p className="mt-4 text-charcoal/75 leading-relaxed">{FOUNDER.homepageBody}</p>
              </Reveal>
              <Reveal delay={0.1}>
                <div className="mt-7 grid sm:grid-cols-2 gap-3">
                  {FOUNDER.highlights.map((h) => (
                    <div key={h} className="flex items-start gap-2.5 text-sm text-charcoal/80">
                      <Check className="h-4.5 w-4.5 h-5 w-5 text-forest shrink-0 mt-0.5" strokeWidth={2} />
                      {h}
                    </div>
                  ))}
                </div>
                <div className="mt-8">
                  <Button to="/about" variant="secondary" data-testid="home-founder-cta">
                    More about Silvertree <ArrowRight className="h-4 w-4" />
                  </Button>
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* METHODOLOGY PREVIEW */}
      <section className="py-20 lg:py-28 bg-charcoal text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-[0.05]" aria-hidden>
          <svg width="100%" height="100%">
            <pattern id="dots" width="32" height="32" patternUnits="userSpaceOnUse">
              <circle cx="2" cy="2" r="1.5" fill="white" />
            </pattern>
            <rect width="100%" height="100%" fill="url(#dots)" />
          </svg>
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <div className="max-w-2xl">
              <Overline light>Our Methodology</Overline>
              <h2 className="mt-4 text-3xl lg:text-4xl font-bold tracking-tight">
                {METHODOLOGY.name}
              </h2>
              <p className="mt-5 text-lg text-white/70 leading-relaxed">
                A structured, disciplined, business-first approach that keeps every engagement
                focused on outcomes.
              </p>
            </div>
          </Reveal>
          <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {METHODOLOGY.phases.map((p, i) => (
              <Reveal key={p.key} delay={(i % 3) * 0.07}>
                <div className="h-full p-7 rounded-xl border border-white/10 bg-white/[0.03] hover:bg-white/[0.06] transition-colors">
                  <div className="flex items-center justify-between">
                    <div className="h-11 w-11 rounded-lg bg-gold/15 text-gold flex items-center justify-center">
                      <Icon name={p.icon} className="h-5 w-5" />
                    </div>
                    <span className="text-sm font-semibold text-white/30">
                      0{i + 1}
                    </span>
                  </div>
                  <h3 className="mt-5 text-lg font-semibold">{p.key}</h3>
                  <p className="mt-2 text-sm text-white/60 leading-relaxed">{p.tagline}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.2}>
            <div className="mt-12">
              <Button to="/methodology" variant="gold" size="lg" data-testid="home-methodology-cta">
                Explore the Methodology <ArrowRight className="h-5 w-5" />
              </Button>
            </div>
          </Reveal>
        </div>
      </section>

      {/* INDUSTRIES */}
      <section className="py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <div className="max-w-2xl">
              <Overline>Industries</Overline>
              <h2 className="mt-4 text-3xl lg:text-4xl font-bold text-charcoal tracking-tight">
                Deep understanding of your operational realities
              </h2>
              <p className="mt-5 text-lg text-charcoal/75 leading-relaxed">
                We bring sector-specific perspective to the industries that power Oklahoma's economy.
              </p>
            </div>
          </Reveal>
          <div className="mt-12 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
            {INDUSTRIES.map((ind, i) => (
              <Reveal key={ind.slug} delay={(i % 5) * 0.05} className="h-full">
                <Link
                  to={`/industries/${ind.slug}`}
                  data-testid={`industry-chip-${ind.slug}`}
                  className="group flex h-full min-h-[128px] flex-col items-start justify-between gap-3 p-5 rounded-xl border border-silver/40 hover:border-forest/40 hover:bg-forest/[0.02] transition-all"
                >
                  <div className="h-10 w-10 rounded-lg bg-forest/8 text-forest flex items-center justify-center group-hover:bg-forest group-hover:text-white transition-colors">
                    <Icon name={ind.icon} className="h-5 w-5" />
                  </div>
                  <span className="text-sm font-medium text-charcoal">{ind.name}</span>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* LOCAL MARKET */}
      <section className="py-20 lg:py-28 bg-[#FAFBFB] border-y border-silver/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <Reveal>
              <div className="relative">
                <img
                  src={IMAGES.okc}
                  alt="Oklahoma City riverfront and downtown skyline"
                  className="rounded-2xl shadow-xl object-cover w-full h-[400px]"
                />
              </div>
            </Reveal>
            <div>
              <Reveal>
                <Overline>Headquartered in Oklahoma · Serving the U.S.</Overline>
                <h2 className="mt-4 text-3xl lg:text-4xl font-bold text-charcoal tracking-tight">
                  Local roots, national reach
                </h2>
                <p className="mt-5 text-lg text-charcoal/75 leading-relaxed">
                  We're proud to call Oklahoma home — and we bring the same practical,
                  relationship-driven service to growing businesses across the United States,
                  on-site or remote.
                </p>
              </Reveal>
              <Reveal delay={0.1}>
                <div className="mt-8 flex flex-wrap gap-2.5">
                  {LOCATIONS.map((loc) => (
                    <span
                      key={loc}
                      className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-white border border-silver/40 text-sm text-charcoal/80"
                    >
                      <MapPin className="h-3.5 w-3.5 text-forest" /> {loc}
                    </span>
                  ))}
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* TESTIMONIAL-STYLE STATEMENT */}
      <section className="py-20 lg:py-28">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Reveal>
            <Quote className="h-10 w-10 text-forest/30 mx-auto" />
            <p className="mt-6 font-editorial text-2xl sm:text-3xl text-charcoal leading-snug">
              We help growing businesses make confident technology decisions — untangling the
              systems that slow you down, protecting what you've built, and turning technology
              from a monthly frustration into a competitive advantage.
            </p>
            <p className="mt-6 text-sm uppercase tracking-[0.2em] text-charcoal/70">
              The Silvertree Approach
            </p>
          </Reveal>
        </div>
      </section>

      <CTASection
        heading="Ready for IT support that actually responds?"
        body="Schedule a 15-minute technology check — a short, no-pressure conversation about your business and what dependable support would look like. Plans from $495/month, including up to 5 users."
        primaryLabel="Schedule a 15-Minute Technology Check"
        primaryTo="/business-it-support#it-support-form"
      />
    </div>
  );
}
